import { useState, useMemo, useEffect } from 'react';
import { Modal, ModalBody, ModalFooter, Button, Spinner } from 'reactstrap';
import { Search, UserPlus, ShieldOff, RotateCcw, Info } from 'react-feather';
import {
  ACCESOS_HEADQUARTER,
  PAGE_SIZE,
  PROPAGACION_AVISO,
  useAccesosLista,
  useCrearAcceso,
  useRevocarAcceso,
} from '../../features/accesos';
import ModalClickable from '../../layouts/modals/ModalClickable';
import Loader from '../../layouts/loader/Loader';
import colors from '../../theme/colors';

// Gestion de la lista de usuarios autorizados (whitelist) contra la API /accesos.
// Solo se muestra a admins (la vista /ajustes ya es solo SUPERADMIN). La lista es
// unica y global: no hay selector de sede, se manda siempre ACCESOS_HEADQUARTER.
//
// OJO: mientras ENFORCE_WHITELIST_GATE este OFF en el servidor, gestionar la lista
// no bloquea a nadie todavia; el bloqueo real se activa en el despliegue final.

// Validacion basica de email en cliente (el backend normaliza y valida de nuevo).
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const norm = (value) => (value ?? '').toString().toLowerCase();

const thStyle = {
  textAlign: 'left',
  fontSize: '0.72rem',
  fontWeight: 600,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: colors.textMuted,
  padding: '10px 14px',
};

const tdStyle = {
  padding: '11px 14px',
  verticalAlign: 'middle',
};

const labelStyle = {
  display: 'block',
  fontSize: '0.8rem',
  fontWeight: 600,
  color: colors.textSecondary,
  marginBottom: '6px',
};

const GestionUsuariosAutorizados = () => {
  const headquarter = ACCESOS_HEADQUARTER;

  const [email, setEmail] = useState('');
  const [nombre, setNombre] = useState('');
  const [formError, setFormError] = useState('');
  const [filtro, setFiltro] = useState('');
  const [page, setPage] = useState(1);
  const [confirmUser, setConfirmUser] = useState(null);
  const [feedback, setFeedback] = useState(null);

  const { data: usuarios = [], isLoading, isError, refetch, isFetching } =
    useAccesosLista(headquarter);
  const crear = useCrearAcceso(headquarter);
  const revocar = useRevocarAcceso(headquarter);

  // Solo se listan los activos; un email revocado se reactiva volviendo a darlo de alta.
  const activos = useMemo(() => usuarios.filter((u) => u.activo === true), [usuarios]);

  const filtrados = useMemo(() => {
    if (!filtro.trim()) return activos;
    const q = norm(filtro);
    return activos.filter(
      (u) => norm(u.email).includes(q) || norm(u.nombre).includes(q),
    );
  }, [activos, filtro]);

  // Reset de pagina al cambiar el filtro o al encoger la lista.
  useEffect(() => {
    setPage(1);
  }, [filtro]);

  const totalPages = Math.max(1, Math.ceil(filtrados.length / PAGE_SIZE));
  const pageSafe = Math.min(page, totalPages);
  const inicio = (pageSafe - 1) * PAGE_SIZE;
  const visibles = filtrados.slice(inicio, inicio + PAGE_SIZE);

  const errorBackend = (error, fallback) =>
    error?.response?.data?.error || fallback;

  const handleAdd = (e) => {
    e.preventDefault();
    const emailNorm = email.trim().toLowerCase();
    if (!EMAIL_RE.test(emailNorm)) {
      setFormError('Introduce un email válido.');
      return;
    }
    if (nombre.length > 255) {
      setFormError('El nombre no puede superar los 255 caracteres.');
      return;
    }
    setFormError('');
    crear.mutate(
      { email: emailNorm, nombre: nombre.trim() || undefined },
      {
        onSuccess: () => {
          setEmail('');
          setNombre('');
          setFeedback({
            color: 'primary',
            header: `Acceso concedido a ${emailNorm}. ${PROPAGACION_AVISO}`,
          });
        },
        onError: (error) => {
          setFeedback({
            color: 'danger',
            header: errorBackend(error, 'No se pudo conceder el acceso.'),
          });
        },
      },
    );
  };

  const confirmarRevocar = () => {
    const objetivo = confirmUser;
    revocar.mutate(
      { id: objetivo.id },
      {
        onSuccess: () => {
          setConfirmUser(null);
          setFeedback({
            color: 'primary',
            header: `Acceso revocado a ${objetivo.email}. ${PROPAGACION_AVISO}`,
          });
        },
        onError: (error) => {
          setConfirmUser(null);
          setFeedback({
            color: 'danger',
            header: errorBackend(error, 'No se pudo revocar el acceso.'),
          });
        },
      },
    );
  };

  return (
    <div>
      <p style={{ color: colors.textMuted, marginTop: 0, marginBottom: '16px', fontSize: '0.8rem' }}>
        Usuarios autorizados a acceder a la aplicación. Añade o revoca accesos por
        email. La lista es única para todas las sedes.
      </p>

      {/* Aviso de propagacion */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '10px 14px',
          marginBottom: '16px',
          borderRadius: '8px',
          border: `1px solid ${colors.border}`,
          background: colors.bg,
          color: colors.textSecondary,
          fontSize: '0.8rem',
        }}
      >
        <Info size={15} style={{ color: colors.info, flexShrink: 0 }} />
        <span>{PROPAGACION_AVISO}</span>
      </div>

      {/* Alta manual */}
      <form onSubmit={handleAdd} style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: 2, minWidth: '220px' }}>
            <label htmlFor="acceso-email" style={labelStyle}>
              Email *
            </label>
            <input
              id="acceso-email"
              type="email"
              className="form-control"
              placeholder="persona@cedetecgroup.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={crear.isPending}
            />
          </div>
          <div style={{ flex: 2, minWidth: '200px' }}>
            <label htmlFor="acceso-nombre" style={labelStyle}>
              Nombre (opcional)
            </label>
            <input
              id="acceso-nombre"
              type="text"
              className="form-control"
              placeholder="Nombre Apellidos"
              value={nombre}
              maxLength={255}
              onChange={(e) => setNombre(e.target.value)}
              disabled={crear.isPending}
            />
          </div>
          <Button color="primary" type="submit" disabled={crear.isPending}>
            {crear.isPending ? (
              <>
                <Spinner size="sm" /> Añadiendo…
              </>
            ) : (
              <>
                <UserPlus size={15} /> Añadir
              </>
            )}
          </Button>
        </div>
        {formError && (
          <div style={{ color: colors.danger, fontSize: '0.8rem', marginTop: '8px' }}>
            {formError}
          </div>
        )}
      </form>

      {/* Buscador + recargar */}
      <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', marginBottom: '16px' }}>
        <div style={{ flex: 1, minWidth: '220px' }}>
          <label htmlFor="acceso-filtro" style={labelStyle}>
            Buscar
          </label>
          <div style={{ position: 'relative' }}>
            <Search
              size={15}
              style={{
                position: 'absolute',
                left: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: colors.textMuted,
              }}
            />
            <input
              id="acceso-filtro"
              type="text"
              className="form-control"
              placeholder="Email o nombre"
              value={filtro}
              onChange={(e) => setFiltro(e.target.value)}
              style={{ paddingLeft: '32px' }}
            />
          </div>
        </div>
        <Button color="light" onClick={() => refetch()} title="Recargar listado" disabled={isFetching}>
          <RotateCcw size={15} />
        </Button>
      </div>

      {/* Listado */}
      {isLoading ? (
        <Loader />
      ) : isError ? (
        <div
          style={{
            padding: '32px',
            textAlign: 'center',
            color: colors.danger,
            border: `1px solid ${colors.border}`,
            borderRadius: '8px',
            background: colors.bgCard,
          }}
        >
          No se pudo cargar la lista de usuarios autorizados.{' '}
          <Button color="link" size="sm" onClick={() => refetch()} style={{ padding: 0 }}>
            Reintentar
          </Button>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          {filtrados.length === 0 ? (
            <div
              style={{
                padding: '32px',
                textAlign: 'center',
                color: colors.textMuted,
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                background: colors.bgCard,
              }}
            >
              {activos.length === 0
                ? 'Todavía no hay usuarios autorizados.'
                : 'No hay usuarios que coincidan con la búsqueda.'}
            </div>
          ) : (
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                background: colors.bgCard,
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                overflow: 'hidden',
                fontSize: '0.875rem',
              }}
            >
              <thead>
                <tr style={{ background: colors.bg }}>
                  <th style={thStyle}>Email</th>
                  <th style={thStyle}>Nombre</th>
                  <th style={thStyle}>Alta</th>
                  <th style={thStyle}>Creado por</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Acción</th>
                </tr>
              </thead>
              <tbody>
                {visibles.map((u) => (
                  <tr key={u.id} style={{ borderTop: `1px solid ${colors.border}` }}>
                    <td style={{ ...tdStyle, fontWeight: 500 }}>{u.email}</td>
                    <td style={{ ...tdStyle, color: colors.textSecondary }}>{u.nombre || '—'}</td>
                    <td style={{ ...tdStyle, color: colors.textMuted, fontVariantNumeric: 'tabular-nums' }}>
                      {u.fechaAlta || '—'}
                    </td>
                    <td style={{ ...tdStyle, color: colors.textSecondary }}>{u.creadoPor || '—'}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      <button
                        type="button"
                        onClick={() => setConfirmUser(u)}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '5px',
                          padding: '6px 12px',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          borderRadius: '6px',
                          border: `1px solid ${colors.danger}`,
                          background: colors.bgCard,
                          color: colors.danger,
                          cursor: 'pointer',
                        }}
                      >
                        <ShieldOff size={13} /> Revocar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Paginacion */}
          {filtrados.length > 0 && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginTop: '12px',
                fontSize: '0.8rem',
                color: colors.textSecondary,
              }}
            >
              <span>
                {inicio + 1}–{Math.min(inicio + PAGE_SIZE, filtrados.length)} de {filtrados.length}
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Button
                  color="light"
                  size="sm"
                  onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                  disabled={pageSafe <= 1}
                >
                  Anterior
                </Button>
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {pageSafe} / {totalPages}
                </span>
                <Button
                  color="light"
                  size="sm"
                  onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                  disabled={pageSafe >= totalPages}
                >
                  Siguiente
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Confirmacion de revocacion */}
      <Modal
        isOpen={confirmUser !== null}
        centered
        toggle={() => !revocar.isPending && setConfirmUser(null)}
      >
        <ModalBody>
          {revocar.isPending ? (
            <h4 className="text-center mt-3">
              <Spinner size="sm" /> Revocando acceso…
            </h4>
          ) : (
            <div className="text-center mt-3">
              <h4>¿Revocar el acceso de {confirmUser?.email}?</h4>
              <p style={{ color: colors.textSecondary, marginBottom: 0 }}>
                Dejará de poder acceder a la aplicación. Puedes volver a concederlo
                añadiéndolo de nuevo.
              </p>
            </div>
          )}
        </ModalBody>
        {!revocar.isPending && (
          <ModalFooter style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button color="secondary" onClick={() => setConfirmUser(null)}>
              Cancelar
            </Button>
            <Button color="danger" onClick={confirmarRevocar}>
              Revocar acceso
            </Button>
          </ModalFooter>
        )}
      </Modal>

      {/* Resultado */}
      {feedback && (
        <ModalClickable
          key={feedback.header}
          header={feedback.header}
          color={feedback.color}
          function={() => setFeedback(null)}
        />
      )}
    </div>
  );
};

export default GestionUsuariosAutorizados;
